import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { pagedQuery, pagedResponse } from './pagination.js';
import { MessageSchema, QtyStringSchema, UnitPrecisionSchema } from './primitives.js';
const c = initContract();
/**
 * Warehouse transfers (INV-07) — moving stock between the company's own
 * locations, instant or two-step:
 *
 *   Instant:  Draft ──Complete──► Completed  (both legs at once; posts NO
 *             journal — same stock account both ends, INV-07 §12)
 *   Two-step: Draft ──Ship──► In Transit ──Receive──► Completed
 *             (Ship writes the OUT legs, value moves into 1133 Stock in
 *             Transit; Receive writes the IN legs — BR-INV-07-06, BR-INV-07-07)
 *   Either:   Draft | In Transit ──Cancel──► Cancelled (cancelling from In
 *             Transit RETURNS the stock — BR-INV-07-15)
 *
 * Status changes ONLY through these actions (BR-INV-07-19 — the free-form
 * selector is gone). Mode editable in Draft only; the partial policy in
 * Draft and In Transit (BR-INV-07-03, BR-INV-07-04). Blank received = full
 * (BR-INV-07-08); over-shipped receive refused (BR-INV-07-09); the
 * discrepancy path is forced when partials are off and a line is short
 * (BR-INV-07-11), writes one adjustment per short line with the reason
 * (BR-INV-07-12, BR-INV-07-13). Over-available WARNS at entry and never blocks
 * (BR-INV-07-14) — the negative-stock rule bites at ship time.
 *
 * A tracked item names its units here exactly as it does on a receipt, an
 * invoice or a delivery note (BR-MD-03-17): `serialNos` / `batchNo` on the
 * draft line say what is travelling, and `receivedSerialNos` on the receive
 * says what arrived. The transfer's own rule is that the second is a SUBSET
 * of the first — BR-INV-07-09 in names rather than in quantities.
 */
// ── The fixed sentences ─────────────────────────────────────────────────────
export const TRANSFER_MESSAGES = {
    /** GEN-59: the cancelling act carries its reason. */
    cancelReasonRequired: 'A transfer can only be cancelled with a reason, which is stored on the transfer and written to the audit log.',
    /**
     * BR-INV-07-01 / ERR-INV-07-01 — and ERR-MD-08-06, which is the SAME
     * refusal seen from the warehouse master's §14 table. The spec words it two
     * ways: BR-INV-07-01 carries an explicit directive — `Message: "Pick two
     * different locations."` — while MD-08 §14's row shows "Pick two different
     * warehouses for a transfer." Only one sentence can render, and the
     * governing BR of the screen the refusal happens on (INV-07, the transfer
     * editor) is the one that states its message AS a message, so it wins; the
     * §14 variant is recorded here rather than silently outvoted. The refusal
     * itself is BR-MD-08-09's, enforced three deep: the CHECK in
     * 20260807540000_stock_transfers.sql, the service throw, and the client's
     * pre-flight — all of them this sentence. Pinned byte-for-byte in
     * `spec-wording.spec.ts`.
     */
    sameLocation: 'Pick two different locations.',
    /** BR-INV-07-02 — a location with transfers switched off. */
    transfersOff: (name) => `${name} does not allow stock transfers.`,
    /** BR-INV-07-09, spec shape. */
    overReceived: (qty, warehouseName) => `You cannot receive more than was shipped. ${qty} left ${warehouseName}.`,
    /**
     * BR-INV-07-09 in its NAMED form, the one rule a transfer has that no other
     * document does: the same units are named twice — once by the sender and
     * once by the receiver — and the second naming must be a subset of the
     * first. A serial that was never put on the van cannot come off it.
     */
    serialNotShipped: (serial, code) => `Serial ${serial} was not shipped on ${code}.`,
    /** BR-INV-07-12. */
    discrepancyReason: 'Give a reason for the discrepancy before completing.',
    /** The §5.6 hint when plain Receive is disabled. */
    useDiscrepancy: "Received differs from shipped — use 'Receive with Discrepancy'.",
    /** INV-07 §15 — an empty transfer cannot ship. */
    noLines: 'Add at least one line before shipping.',
    /** The §5.5 over-available warning banner (warns, never blocks) — ERR-INV-07-02. */
    overAvailable: 'Some quantities exceed available stock',
    /** A workflow action fired from the wrong state. */
    wrongState: 'This transfer has already moved on. Refresh to see its current state.',
    /** INV-07 §15 "two users at once": cancel vs receive — first to commit wins. */
    raced: 'Somebody else changed this transfer first. Refresh to see what happened.',
};
/**
 * D46 — INV-07's three parameterised transfer refusals, structured.
 * `transfersOff` carries a location name, `overReceived` a PRE-FORMATTED
 * quantity string and a warehouse name, `serialNotShipped` a serial and a
 * transfer code. No token, no plural. Sentence parity pinned by
 * `refusal-census.spec.ts`.
 */
export const TRANSFER_REFUSALS = {
    transfersOff: (name) => ({
        message: TRANSFER_MESSAGES.transfersOff(name),
        key: 'trf.errTransfersOff',
        params: { name },
    }),
    overReceived: (qty, warehouseName) => ({
        message: TRANSFER_MESSAGES.overReceived(qty, warehouseName),
        key: 'trf.errOverReceived',
        params: { qty, warehouseName },
    }),
    serialNotShipped: (serial, code) => ({
        message: TRANSFER_MESSAGES.serialNotShipped(serial, code),
        key: 'trf.errSerialNotShipped',
        params: { serial, code },
    }),
};
// ── Shapes ──────────────────────────────────────────────────────────────────
export const TransferModeSchema = z.enum(['instant', 'two_step']);
export const TransferStatusSchema = z.enum(['draft', 'in_transit', 'completed', 'cancelled']);
export const StockTransferLineSchema = z
    .object({
    id: z.uuid(),
    lineNo: z.number().int(),
    itemId: z.uuid(),
    itemCode: z.string(),
    itemName: z.string(),
    unitSymbol: z.string(),
    /**
     * `N-4`'s other half. A transfer line has no unit of its own — the input
     * schema takes an item and a quantity and nothing else — so this is the
     * item's STOCK unit precision, and `BR-MD-03-04d`'s line-versus-stock
     * distinction cannot bite here. Without it the grid printed "40" for a
     * shelf measured in thousandths.
     */
    unitPrecision: UnitPrecisionSchema,
    /** Available at source NOW (draft) — the §5.5 warning column. */
    availableAtSource: z.string(),
    qty: z.string(),
    /** NULL = received in full (BR-INV-07-08). */
    receivedQty: z.string().nullable(),
    /** shipped − received, floored at zero, only when partials allowed. */
    pending: z.string().nullable(),
    /** Stamped at ship so value out = value in exactly. */
    unitCost: z.string().nullable(),
    /** BR-MD-03-17: the units TRAVELLING — one serial each, or the lot. */
    serialNos: z.string().array().nullable(),
    batchNo: z.string().nullable(),
    /** The units that ARRIVED. NULL = everything shipped arrived. */
    receivedSerialNos: z.string().array().nullable(),
})
    .strict();
export const StockTransferSchema = z
    .object({
    id: z.uuid(),
    /** TRF-####. */
    code: z.string(),
    transferNo: z.number().int(),
    transferDate: z.string(),
    fromWarehouseId: z.uuid(),
    fromWarehouseName: z.string(),
    toWarehouseId: z.uuid(),
    toWarehouseName: z.string(),
    mode: TransferModeSchema,
    allowPartial: z.boolean(),
    status: TransferStatusSchema,
    requestedBy: z.string(),
    reference: z.string().nullable(),
    carrier: z.string().nullable(),
    driver: z.string().nullable(),
    vehicleNo: z.string().nullable(),
    expectedArrival: z.string().nullable(),
    actualArrival: z.string().nullable(),
    discrepancyReason: z.string().nullable(),
    notes: z.string().nullable(),
    shippedAt: z.string().nullable(),
    receivedAt: z.string().nullable(),
    cancelledAt: z.string().nullable(),
    /** GEN-59: why the transfer was cancelled — null until it is, and on rows cancelled before the reason existed. */
    cancelReason: z.string().nullable(),
    /** Total units currently on the van (the amber banner while In Transit). */
    inTransitQty: z.string(),
    totalQty: z.string(),
    lines: StockTransferLineSchema.array(),
    createdAt: z.string(),
    updatedAt: z.string(),
})
    .strict();
const TransferLineInputSchema = z
    .object({
    itemId: z.uuid(),
    qty: QtyStringSchema,
    /**
     * BR-MD-03 tracking capture, the same shape as every other document that
     * moves stock: one serial per unit travelling, or the lot number. Named
     * while the transfer is a draft — the units leave the source when it
     * ships, and a unit that leaves unnamed is lost.
     */
    serialNos: z.string().trim().min(1).max(60).array().max(1000).optional(),
    batchNo: z.string().trim().min(1).max(40).optional(),
})
    .strict();
export const CreateStockTransferRequestSchema = z
    .object({
    transferDate: z.iso.date(),
    fromWarehouseId: z.uuid(),
    toWarehouseId: z.uuid(),
    /** Absent = the company default (Instant / partials on) — the §5.2 override pattern. */
    mode: TransferModeSchema.optional(),
    allowPartial: z.boolean().optional(),
    requestedBy: z.string().trim().min(1).max(120),
    reference: z.string().trim().max(200).nullable().optional(),
    carrier: z.string().trim().max(120).nullable().optional(),
    driver: z.string().trim().max(120).nullable().optional(),
    vehicleNo: z.string().trim().max(60).nullable().optional(),
    expectedArrival: z.iso.date().nullable().optional(),
    notes: z.string().max(2000).nullable().optional(),
    lines: TransferLineInputSchema.array().min(1).max(200),
})
    .strict();
export const UpdateStockTransferRequestSchema = CreateStockTransferRequestSchema.partial().extend({
    actualArrival: z.iso.date().nullable().optional(),
});
/** A received quantity: zero allowed (nothing arrived), 3 decimals. */
const ReceivedQtySchema = z
    .string()
    .regex(/^\d{1,12}(\.\d{1,3})?$/, 'Received quantities are decimal strings with at most 3 decimals');
export const ReceiveTransferRequestSchema = z
    .object({
    /** Absent line = received in full (BR-INV-07-08). */
    lines: z
        .object({
        lineId: z.uuid(),
        receivedQty: ReceivedQtySchema,
        /**
         * Which units arrived, for a serial-tracked line — absent means the
         * ones shipped, which is blank-means-full said in names. Every one of
         * them must have been on the van (TRANSFER_MESSAGES.serialNotShipped).
         */
        receivedSerialNos: z.string().trim().min(1).max(60).array().max(1000).optional(),
    })
        .strict()
        .array()
        .max(200)
        .optional(),
    /** Required exactly when partials are off and a line is short (BR-INV-07-11, BR-INV-07-12). */
    discrepancyReason: z.string().trim().max(500).optional(),
    actualArrival: z.iso.date().nullable().optional(),
})
    .strict();
const transferParams = z.object({ transferId: z.uuid() }).strict();
const workflowResponses = {
    200: StockTransferSchema,
    400: MessageSchema,
    401: MessageSchema,
    403: MessageSchema,
    404: MessageSchema,
    409: MessageSchema,
};
export const stockTransfersContract = c.router({
    list: {
        method: 'GET',
        path: '/api/stock-transfers',
        query: pagedQuery({
            status: TransferStatusSchema.optional(),
            /** EITHER end of the move — the OR is a predicate, not a post-filter. */
            warehouseId: z.uuid().optional(),
        }),
        responses: {
            200: pagedResponse('transfers', StockTransferSchema),
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'Transfers (inventory.view), newest first, PAGED (NFR-42) — both filters in SQL.',
    },
    get: {
        method: 'GET',
        path: '/api/stock-transfers/:transferId',
        pathParams: transferParams,
        responses: {
            200: StockTransferSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'One transfer with its lines and live availability (inventory.view).',
    },
    create: {
        method: 'POST',
        path: '/api/stock-transfers',
        body: CreateStockTransferRequestSchema,
        responses: {
            201: StockTransferSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'New draft (inventory.create): TRF-#### advances on every new document (BR-INV-07-17, BR-INV-07-18); two different, active, transfer-enabled locations (BR-INV-07-01, BR-INV-07-02, BR-INV-04-05); over-available warns, never blocks (BR-INV-07-14).',
    },
    update: {
        method: 'PATCH',
        path: '/api/stock-transfers/:transferId',
        pathParams: transferParams,
        body: UpdateStockTransferRequestSchema,
        responses: workflowResponses,
        summary: 'Edit (inventory.edit): a DRAFT freely (BR-INV-07-21); In Transit only the partial policy and logistics (BR-INV-07-03, BR-INV-07-04) — the DB freezes the rest.',
    },
    remove: {
        method: 'DELETE',
        path: '/api/stock-transfers/:transferId',
        pathParams: transferParams,
        body: c.noBody(),
        responses: {
            204: c.noBody(),
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Delete a DRAFT (inventory.delete). Anything shipped is history and stays.',
    },
    ship: {
        method: 'POST',
        path: '/api/stock-transfers/:transferId/ship',
        pathParams: transferParams,
        body: z.object({}).strict().optional(),
        responses: workflowResponses,
        summary: 'Ship a two-step draft (inventory.approve): writes the Transfer Out legs at source cost, moves value into 1133 (BR-INV-07-06); source stock is down NOW. Negative-stock rule bites here (BR-INV-04-08), and a tracked line must name the units leaving (BR-MD-03-17) — one movement per serial.',
    },
    complete: {
        method: 'POST',
        path: '/api/stock-transfers/:transferId/complete',
        pathParams: transferParams,
        body: z.object({}).strict().optional(),
        responses: workflowResponses,
        summary: 'Complete an INSTANT draft (inventory.approve): both legs at once, one shared TRF reference (BR-INV-06-04); posts no journal (INV-07 §12).',
    },
    receive: {
        method: 'POST',
        path: '/api/stock-transfers/:transferId/receive',
        pathParams: transferParams,
        body: ReceiveTransferRequestSchema,
        responses: workflowResponses,
        summary: 'Receive an in-transit transfer (inventory.approve): blank = full (BR-INV-07-08), never above shipped (BR-INV-07-09) and never a unit that was not shipped; partials-off + short forces the discrepancy path with a reason, one adjustment per short line naming the units that went missing (BR-INV-07-11, BR-INV-07-12, BR-INV-07-13); partials-on shortfall returns to source, named, so nothing stays in transit (BR-INV-07-16).',
    },
    cancel: {
        method: 'POST',
        path: '/api/stock-transfers/:transferId/cancel',
        pathParams: transferParams,
        /** GEN-59: never empty — the reason is the act's price of admission. */
        body: z
            .object({
            reason: z.string().trim().min(1, TRANSFER_MESSAGES.cancelReasonRequired).max(500),
        })
            .strict(),
        responses: workflowResponses,
        summary: 'Cancel (inventory.edit): a draft just stops; from In Transit the stock RETURNS to the source (BR-INV-07-15, BR-INV-07-16) — cancel undoes a shipment, it never creates value. The stepper empties completely. GEN-59: reason required, stored on the transfer and written to the audit row.',
    },
}, { strictStatusCodes: true });
//# sourceMappingURL=stock-transfers.js.map